import { ListTable, useFormat } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, useReportRange } from './reportRange';

type Staff = HoComponents['schemas']['StaffSalesDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);
const share = (part: number, whole: number) => (whole === 0 ? '' : `${((part / whole) * 100).toFixed(1)}%`);

/**
 * Staff performance (REPORTS.md B4): per staff member who opened paid orders, their orders, guests, sales, average check, items
 * per guest, and the voids and discounts they made, as a share of their sales.
 */
export function StaffReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const r = (key: string) => t(`reports.staff.${key}` as I18nKey);
  const staff = useQuery({
    queryKey: ['reports', 'staff', range],
    queryFn: () => unwrap<Staff[]>(hoApi.GET('/api/v1/reporting/staff', { params: { query: rangeQuery(range) } })),
  });
  const rows = staff.data ?? [];
  const name = (s: Staff) => s.name || r('unknown');

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={setRange}
      error={staff.error}
      csvName="staff-performance"
      csv={
        rows.length > 0
          ? () => [
              [
                r('name'),
                t('reports.orders'),
                t('reports.guests'),
                t('reports.total'),
                t('reports.averageCheck'),
                r('itemsPerGuest'),
                t('reports.voids'),
                r('voidAmount'),
                t('reports.discounts'),
                r('discountAmount'),
              ],
              ...rows.map((s) => [
                name(s),
                n(s.orders),
                n(s.guests),
                n(s.sales),
                n(s.averageCheck),
                s.itemsPerGuest == null ? '' : n(s.itemsPerGuest),
                n(s.voids),
                n(s.voidAmount),
                n(s.discounts),
                n(s.discountAmount),
              ]),
            ]
          : undefined
      }
    >
      <ListTable<Staff & { id: string }>
        title={r('byStaff')}
        description={r('note')}
        columns={[
          { key: 'name', header: r('name'), render: name },
          { key: 'orders', header: t('reports.orders'), numeric: true, render: (s) => format.number(n(s.orders)) },
          { key: 'guests', header: t('reports.guests'), numeric: true, render: (s) => format.number(n(s.guests)) },
          { key: 'sales', header: t('reports.total'), numeric: true, render: (s) => format.number(n(s.sales)) },
          {
            key: 'averageCheck',
            header: t('reports.averageCheck'),
            numeric: true,
            render: (s) => format.number(n(s.averageCheck)),
          },
          {
            key: 'itemsPerGuest',
            header: r('itemsPerGuest'),
            numeric: true,
            render: (s) => (s.itemsPerGuest == null ? '' : format.number(n(s.itemsPerGuest))),
          },
          {
            key: 'voids',
            header: t('reports.voids'),
            numeric: true,
            render: (s) =>
              n(s.voids) === 0
                ? ''
                : `${format.number(n(s.voids))} · ${format.number(n(s.voidAmount))} · ${share(n(s.voidAmount), n(s.sales))}`,
          },
          {
            key: 'discounts',
            header: t('reports.discounts'),
            numeric: true,
            render: (s) =>
              n(s.discounts) === 0
                ? ''
                : `${format.number(n(s.discounts))} · ${format.number(n(s.discountAmount))} · ${share(n(s.discountAmount), n(s.sales))}`,
          },
        ]}
        rows={rows.map((s, i) => ({ ...s, id: s.userId ?? `none-${i}` }))}
        ariaLabel={r('title')}
        loading={staff.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
    </ReportFrame>
  );
}
